import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import SEO from '../components/SEO';
import HeroCanvas from '../components/HeroCanvas';
import Marquee from '../components/Marquee';
import StatCounter from '../components/StatCounter';
import { Reveal, Eyebrow, MaskedLine } from '../components/Reveal';
import api from '../lib/api';
import { SERVICES, EXPERIENCE_STATS, WORK_AREAS, APPROACH_STAGES, INSIGHT_TEASERS, IMAGES } from '../data/content';

function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yContent = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden bg-brand-deep" data-testid="home-hero">
      <HeroCanvas />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(23,107,58,0.16),transparent_55%)]" />
      <motion.div style={{ y: yContent, opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-36 pb-24 lg:px-8">
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-brand-green"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-brand-green" />
          A Division of Kalpataru Enterprise
        </motion.p>
        <h1 className="max-w-5xl font-heading text-4xl font-extrabold leading-[1.06] tracking-tight text-brand-offwhite sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          <MaskedLine delay={0.25}>Turning <span className="text-brand-green">Insights</span> into</MaskedLine>
          <MaskedLine delay={0.4}>Strategic Advantage.</MaskedLine>
        </h1>
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg"
          data-testid="hero-subtext"
        >
          Research-driven consulting, market intelligence, and strategic advisory solutions that help organisations navigate complexity, unlock opportunities, and make evidence-based decisions.
        </motion.p>
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/services"
            data-testid="hero-cta-explore-services"
            className="group flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 font-heading text-sm font-bold text-white transition-colors duration-300 hover:bg-brand-deepgreen"
          >
            Explore Our Services
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-cta-lets-talk"
            className="group flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-heading text-sm font-bold text-white transition-colors duration-300 hover:border-brand-green hover:text-brand-green"
          >
            Let's Talk
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </motion.div>
      <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
        <motion.div
          animate={reduce ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="flex h-10 w-6 items-start justify-center rounded-full border border-white/25 p-1.5"
        >
          <span className="h-2 w-1 rounded-full bg-brand-green" />
        </motion.div>
      </div>
    </section>
  );
}

function WhoWeAre() {
  return (
    <section className="bg-white" data-testid="home-who-we-are">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 py-24 lg:grid-cols-2 lg:gap-20 lg:px-8 lg:py-32">
        <Reveal>
          <div className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border-2 border-brand-green/30" aria-hidden="true" />
            <img
              src={IMAGES.aboutEditorial}
              alt="Modern architectural perspective representing structured strategic thinking"
              className="relative aspect-[4/5] w-full rounded-2xl object-cover sm:aspect-[5/4] lg:aspect-[4/5]"
              loading="lazy"
              data-testid="who-we-are-image"
            />
            <div className="absolute -bottom-6 right-4 rounded-xl bg-brand-deep px-6 py-5 shadow-xl lg:-right-6">
              <p className="font-heading text-3xl font-extrabold text-brand-green">7+</p>
              <p className="mt-1 max-w-[160px] text-[11px] leading-snug text-white/60">Years of institutional experience through Kalpataru Enterprise</p>
            </div>
          </div>
        </Reveal>
        <div className="flex flex-col justify-center">
          <Reveal><Eyebrow>Who We Are</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl lg:text-[2.75rem] lg:leading-[1.15]">
              Insight-Led Thinking. Evidence-Driven Strategy.
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 text-base leading-relaxed text-brand-body">
              INSIGHTS STRATEGY is a multidisciplinary research, management consulting, and strategic advisory organisation operating as a division of Kalpataru Enterprise.
            </p>
            <p className="mt-4 text-base leading-relaxed text-brand-body">
              Drawing upon approximately seven years of institutional experience through its parent organisation, INSIGHTS STRATEGY builds upon a legacy of technical and commercial infrastructure feasibility studies while extending into market intelligence, management consulting, public policy research, technology advisory, socioeconomic research, and institutional capacity building.
            </p>
            <p className="mt-4 text-base leading-relaxed text-brand-body">
              Our approach combines analytical rigour, contextual understanding, and practical business insights to help organisations make informed strategic decisions.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <Link to="/about" data-testid="who-we-are-cta" className="group mt-8 inline-flex items-center gap-2 font-heading text-sm font-bold text-brand-deepgreen">
              Discover Our Story
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Expertise() {
  return (
    <section className="bg-brand-offwhite" data-testid="home-expertise">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Our Expertise</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">What We Do</h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-4 text-lg text-brand-body">Integrated consulting and research capabilities designed to transform complexity into clarity.</p>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} delay={0.06 * i} className={i === 6 ? 'sm:col-span-2 lg:col-span-1' : ''}>
                <Link
                  to={`/services#${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-brand-grey bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-green/40 hover:shadow-[0_20px_50px_rgba(11,13,11,0.08)]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-offwhite text-brand-deepgreen transition-colors duration-300 group-hover:bg-brand-green group-hover:text-white">
                    <Icon size={22} strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-6 font-heading text-lg font-bold leading-snug text-brand-black">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-body">{s.short}</p>
                  <span className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-brand-deepgreen">
                    Learn More
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Experience() {
  return (
    <section className="bg-brand-deep" data-testid="home-experience">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="max-w-2xl">
          <Reveal><Eyebrow dark>Our Experience</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">Experience That Informs Decisions.</h2>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-6">
          {EXPERIENCE_STATS.map((s, i) => (
            <Reveal key={s.label} delay={0.08 * i}>
              <StatCounter value={s.value} suffix={s.suffix} text={s.text} label={s.label} note={s.note} dark />
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-14 max-w-3xl text-xs leading-relaxed text-white/40">
            Institutional experience is drawn from the parent organisation, Kalpataru Enterprise, and is presented separately from engagements delivered under the INSIGHTS STRATEGY brand. Client figures reflect the institutional footprint and are subject to verification before publication.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function SelectedWork() {
  return (
    <section className="bg-white" data-testid="home-selected-work">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Reveal><Eyebrow>Selected Work</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">
                Our Work Across Markets, Industries and Institutions
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/case-studies" data-testid="selected-work-view-all" className="group inline-flex items-center gap-2 font-heading text-sm font-bold text-brand-deepgreen">
              View Case Studies
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WORK_AREAS.map((w, i) => (
            <Reveal key={w.slug} delay={0.06 * i}>
              <Link
                to="/case-studies"
                data-testid={`work-card-${w.slug}`}
                className="group block overflow-hidden rounded-2xl border border-brand-grey bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(11,13,11,0.1)]"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={w.image} alt={w.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute left-4 top-4 rounded-full bg-brand-deep/85 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-green backdrop-blur-sm">
                    {w.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg font-bold leading-snug text-brand-black">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-body">{w.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Approach() {
  return (
    <section className="bg-brand-offwhite" data-testid="home-approach">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Our Approach</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">Evidence. Strategy. Impact.</h2>
          </Reveal>
        </div>
        <div className="relative mt-16 grid gap-12 lg:grid-cols-3 lg:gap-8">
          <div className="absolute left-0 right-0 top-7 hidden h-px bg-brand-grey lg:block" aria-hidden="true" />
          {APPROACH_STAGES.map((s, i) => (
            <Reveal key={s.stage} delay={0.12 * i}>
              <div className="relative" data-testid={`approach-stage-${s.name.toLowerCase()}`}>
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-brand-green bg-brand-offwhite font-heading text-sm font-extrabold text-brand-deepgreen">
                  {s.stage}
                </div>
                <h3 className="mt-7 font-heading text-2xl font-bold text-brand-black">{s.name}</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-brand-body">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function InsightsPreview() {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.get('/insights').then((r) => setArticles(r.data.slice(0, 3))).catch(() => setArticles([]));
  }, []);

  const hasArticles = articles && articles.length > 0;

  return (
    <section className="bg-white" data-testid="home-insights">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Reveal><Eyebrow>Insights &amp; Perspectives</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">Ideas That Inform the Future</h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/insights" data-testid="insights-view-all" className="group inline-flex items-center gap-2 font-heading text-sm font-bold text-brand-deepgreen">
              Visit the Insights Hub
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        {hasArticles ? (
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((a, i) => (
              <Reveal key={a.insight_id} delay={0.06 * i}>
                <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-grey bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(11,13,11,0.1)]">
                  {a.cover_image && (
                    <div className="h-48 overflow-hidden">
                      <img src={a.cover_image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-deepgreen">{a.category}</span>
                    <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-brand-black">{a.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-body">{a.summary}</p>
                    <span className="mt-5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-brand-deepgreen">
                      Read More <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INSIGHT_TEASERS.map((t, i) => (
              <Reveal key={t.category} delay={0.06 * i}>
                <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-grey bg-brand-offwhite" data-testid={`insight-teaser-${i}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={t.image} alt={t.category} loading="lazy" className="h-full w-full object-cover opacity-80" />
                    <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-deepgreen backdrop-blur-sm">
                      Forthcoming
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-heading text-lg font-bold leading-snug text-brand-black">{t.category}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-body">{t.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-brand-deep" data-testid="home-final-cta">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(88,169,52,0.14),transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-5 py-28 lg:px-8 lg:py-36">
        <Reveal>
          <h2 className="max-w-3xl font-heading text-3xl font-extrabold tracking-tight text-white sm:text-5xl lg:leading-[1.1]">
            The Right Insight Can Change <span className="text-brand-green">Everything.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg">
            Let's explore how evidence, analysis, and strategic thinking can support your organisation's next important decision.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <Link
            to="/contact"
            data-testid="final-cta-start-conversation"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-4 font-heading text-sm font-bold text-white transition-colors duration-300 hover:bg-brand-deepgreen"
          >
            Start a Conversation
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="INSIGHTS STRATEGY | Research, Management Consulting & Strategic Advisory"
        description="INSIGHTS STRATEGY, a division of Kalpataru Enterprise, provides management consulting, market research, policy advisory, technology research and capacity-building services across India."
        path="/"
      />
      <Hero />
      <Marquee />
      <WhoWeAre />
      <Expertise />
      <Experience />
      <SelectedWork />
      <Approach />
      <InsightsPreview />
      <FinalCTA />
    </>
  );
}
